import { Group, Paper, Stack, Text } from "@mantine/core"
import { IconHeartHandshake } from "@tabler/icons-react"
import { CertificateStatus } from "@tfg/db"
import { Accordion, CountryCell, DocumentActionsCell } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { fetchDocumentUrl } from "~/hooks/useDocumentsQuery"
import { CertificateStatusBadge } from "~/pages/Brand/Certificates/components/CertificateStatusBadge"
import { COUNTRIES } from "~/utils/countries"
import { useGetOperations } from "~/utils/operations"

interface Props {
  certificates: LibraryResponses.Product["standards"][number]["certificates"]
}

export function isCertificateVerified(
  certificate: LibraryResponses.Product["standards"][number]["certificates"][number]
) {
  return certificate.status === CertificateStatus.PROCESSED
}

export const CertificatesCell = ({ certificates }: Props) => {
  if (!certificates.length) return <Text c="gray">-</Text>

  const totalCertificates = certificates.length
  const completedCertificates = certificates.filter((certificate) =>
    isCertificateVerified(certificate)
  ).length
  return (
    <Stack onClick={(e) => e.stopPropagation()} miw={500}>
      <Accordion variant="filled">
        <Accordion.Item value="certificate">
          <Accordion.Control>
            <Group justify="space-between">
              <Text
                c={
                  completedCertificates === totalCertificates
                    ? "green"
                    : completedCertificates
                      ? "yellow"
                      : "red"
                }
              >
                <strong>
                  {completedCertificates} /{totalCertificates}
                </strong>
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {certificates.map((certificate, index) => (
                <CertificateRow key={index} certificate={certificate} />
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  )
}

const CertificateRow = ({
  certificate,
}: {
  certificate: LibraryResponses.Product["standards"][number]["certificates"][number]
}) => {
  const { t } = useTranslation(["products", "common"])
  const operations = useGetOperations()

  const activityInfo =
    certificate.activityName === "DIRECT_SUPPLIER"
      ? {
          icon: IconHeartHandshake,
          name: t("products:lists.certificates.directSupplier"),
        }
      : certificate.activityName
        ? operations[certificate.activityName]
        : undefined

  return (
    <Paper p="xs">
      <Stack>
        <Group justify="space-between">
          <Text>
            <strong>{certificate.name}</strong>
          </Text>
          <Group gap="xs" justify="right">
            {certificate.documentId ? (
              <DocumentActionsCell
                documentId={certificate.documentId}
                documentName={certificate.name}
                fetchViewUrl={() => fetchDocumentUrl(certificate.documentId)}
                fetchDownloadUrl={() =>
                  fetchDocumentUrl(certificate.documentId, true)
                }
                missingDocumentText={t("common:documentUpload.missingDocument")}
              />
            ) : null}
            <CertificateStatusBadge status={certificate.status} />
          </Group>
        </Group>
        {certificate.productionSite && (
          <>
            <Group gap={3}>
              {activityInfo &&
                typeof activityInfo !== "string" &&
                activityInfo.icon && <activityInfo.icon size={20} />}
              {activityInfo &&
                typeof activityInfo !== "string" &&
                activityInfo.name && <p>{activityInfo.name}</p>}
              {certificate.item?.name && " : "}
              {certificate.item?.name}
            </Group>
            <Group>
              <CountryCell
                size="xs"
                countriesMapper={COUNTRIES}
                countryIso3166={certificate.productionSite?.countryIso3166}
              />
              {!certificate.productionSite.name ? (
                <Text c="gray" size="xs">
                  {t("products:lists.certificates.confidential")}
                </Text>
              ) : (
                <>
                  <Text>{certificate.productionSite?.name}</Text>
                  <Text c="gray">
                    {certificate.productionSite?.address === ""
                      ? t("products:lists.certificates.noAddressProvided")
                      : certificate.productionSite?.address}
                  </Text>
                </>
              )}
            </Group>
          </>
        )}
      </Stack>
    </Paper>
  )
}
